<template>
  <div :aria-busy="isLoading">
    <p v-if="isLoading" role="status" class="text-muted">{{ $t('asyncFeedback.loading') }}</p>
    <div v-if="errorState" role="alert" class="alert alert-danger">
      {{ errorMessage }}
      <button type="button" class="btn btn-outline-primary" @click="loadTicket()">{{ $t('asyncFeedback.retry') }}</button>
      <button type="button" class="btn btn-outline-primary" @click="closeDetail">{{ $t('ticketRoute.back') }}</button>
    </div>
    <component
      v-if="report && !errorState && loadedTicketId === ticketId"
      :is="detailComponent"
      :key="detailRole + ':' + loadedTicketId"
      v-bind="detailProps"
      @refresh="refreshDetail"
      @close="closeDetail"
    />
  </div>
</template>

<script>
import Service from '@/service/api';
import AdminReportDetail from '@/pages/admin/ReportDetail.vue';
import TechnicianReportDetail from '@/pages/technician/ReportDetail.vue';
import SupervisorReportDetail from '@/pages/supervisor/ReportDetail.vue';

export default {
  name: 'TicketDetailRoute',
  components: { AdminReportDetail, TechnicianReportDetail, SupervisorReportDetail },
  props: {
    ticketId: { type: String, required: true },
    detailRole: { type: String, required: true, validator: role => ['admin', 'supervisor', 'technician'].includes(role) },
  },
  data() {
    return { report: null, loadedTicketId: null, isLoading: false, errorState: '' };
  },
  computed: {
    routeKey() { return this.detailRole + ':' + this.ticketId; },
    detailComponent() { return this.detailRole === 'admin' ? 'AdminReportDetail' : this.detailRole === 'supervisor' ? 'SupervisorReportDetail' : 'TechnicianReportDetail'; },
    detailProps() {
      return this.detailRole === 'technician'
        ? { report: this.report, routeManaged: true }
        : { report: this.report };
    },
    errorMessage() {
      const key = this.errorState === 'forbidden' ? 'mgmt.accessDenied'
        : this.errorState === 'not-found' ? 'ticketRoute.notFound' : 'mgmt.loadFail';
      return this.$t(key);
    },
  },
  watch: {
    routeKey: {
      immediate: true,
      handler() {
        this.report = null;
        this.loadedTicketId = null;
        this.loadTicket();
      },
    },
  },
  beforeDestroy() {
    this._detailDisposed = true;
    this._ticketRequest = null;
  },
  methods: {
    isCurrent(request) {
      return !this._detailDisposed && !this._closing && this._ticketRequest === request
        && request.id === this.ticketId && request.role === this.detailRole;
    },
    failureState(response) {
      if (response?.status === 403) return 'forbidden';
      // The existing GET service throws "Ticket not found", which the current
      // controller returns as 500. Also support its normal 400/404 responses.
      if ([400, 404].includes(response?.status) || response?.data?.message === 'Ticket not found') return 'not-found';
      return 'error';
    },
    loadTicket(refresh = false) {
      if (this._detailDisposed || this._closing) return Promise.resolve();
      const pending = this._ticketRequest;
      if (pending && pending.id === this.ticketId && pending.role === this.detailRole) {
        if (refresh) pending.refreshAgain = true;
        return pending.promise;
      }

      const request = { id: this.ticketId, role: this.detailRole, refreshAgain: false, promise: null };
      this._ticketRequest = request;
      this.isLoading = true;
      this.errorState = '';
      request.promise = Promise.resolve().then(async () => {
        try {
          do {
            if (!this.isCurrent(request)) break;
            request.refreshAgain = false;
            try {
              if (!request.id) {
                this.errorState = 'not-found';
                break;
              }
              const response = await Service.ticket('get', null, { urlAppend: `/${encodeURIComponent(request.id)}` });
              if (!this.isCurrent(request) || request.refreshAgain) continue;
              const ticket = response.data?.data;
              if (response.data?.success !== true) {
                this.errorState = this.failureState(response);
                this.report = null;
              } else if (ticket === null) {
                this.errorState = 'not-found';
                this.report = null;
              } else if (!ticket || Array.isArray(ticket) || String(ticket._id || ticket.id).toLowerCase() !== request.id.toLowerCase()) {
                this.errorState = 'error';
                this.report = null;
              } else {
                this.report = ticket;
                this.loadedTicketId = request.id;
              }
            } catch (error) {
              if (!this.isCurrent(request) || request.refreshAgain) continue;
              this.report = null;
              this.errorState = this.failureState(error.response);
            }
          } while (this.isCurrent(request) && request.refreshAgain);
        } finally {
          if (this.isCurrent(request)) {
            this._ticketRequest = null;
            this.isLoading = false;
          }
        }
      });
      return request.promise;
    },
    refreshDetail() {
      // Keep the child mounted for accept/ETA refreshes, preserving its step.
      return this.loadTicket(true);
    },
    closeDetail() {
      if (this._closing || this._detailDisposed) return;
      this._closing = true;
      this._ticketRequest = null;
      const history = this.$route.query.tab === 'history';
      const path = history ? `/${this.detailRole}/history`
        : this.detailRole === 'admin' ? '/admin/mainadmin' : this.detailRole === 'supervisor' ? '/supervisor/mainsupervisor' : '/technician/maintechnician';
      return this.$router.replace(path);
    },
  },
};
</script>
